import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AppService } from '../../app.service';
import { ApiRequestService } from '../../shared/api-request.service';
import { UtilsService } from '../../shared/utils.service';
import { tap } from 'rxjs/operators';
import { NetworkedUsersSelectorComponent } from '../../shared/networked-users-selector/networked-users-selector.component';
import { UserPublicProfileComponent } from '../../shared/user-public-profile/user-public-profile.component';
import { ApiDataSource } from '../../utils/api-data-source';
import { AssetModel } from '../../models/asset.model';
import { AssetsViewUsersFilterComponent } from '../assets-view-users-filter/assets-view-users-filter.component';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-assets-view-users',
  templateUrl: './assets-view-users.component.html',
  styleUrls: ['./assets-view-users.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AssetsViewUsersComponent implements OnInit, AfterViewInit {
  @Input('asset_id') asset_id: number;
  @Input('asset') asset: AssetModel;

  dataSource: AssetsViewUsersDataSource = new AssetsViewUsersDataSource(
    this.app,
    this.api
  );

  // Built dynamically in ngOnInit(). The checkbox column only drives Unlink Selected, so it is
  // added for a user who holds the asset edit permission and omitted otherwise.
  listColumns: string[] = [];

  // the paginator and sorter
  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  // RBAC permission flag for the link and unlink actions. Managing an asset's related records is
  // part of the asset workflow, so it is governed by the asset edit permission rather than by the
  // permissions of the related feature. Resolved once in ngOnInit().
  canEdit: boolean = false;

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_REGISTERS_ASSETS,
          RBACService.RBAC_A.A_EDIT
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_ASSETS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      false
    );

    this.listColumns = [];
    if (this.canEdit) {
      this.listColumns.push('select');
    }
    this.listColumns.push(
      'id',
      'contact_person',
      'email',
      'mobile',
      'trade',
      'date_created',
      // 'date_created_UTC',
      'actions'
    );

    this.dataSource.asset_id = this.asset_id;
    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting takes place
    this.sort.sortChange.subscribe(() => {
      this.paginator1.pageIndex = 0;
      this.paginator2.pageIndex = 0;
    });

    const paginatorTap = tap((paginator) => {
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      this.dataSource.limit = paginator['pageSize'];
      this.dataSource.offset = paginator['pageIndex'];
      this.dataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange
      .pipe(
        tap((sorter) => {
          this.dataSource.order_by = sorter['active'];
          this.dataSource.order = sorter['direction'];

          // Sort UTC dates by their corresponding date columns.
          if (sorter['active'] == 'date_created_UTC') {
            this.dataSource.order_by = 'date_created';
          }

          this.dataSource.getData();
        })
      )
      .subscribe();
  }

  /**
   * Display a component dialog for managing users.
   *
   * @return {void}
   */
  onManageUsers(): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to manage the users linked to this asset.'
      );
      return;
    }

    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        selected: this.dataSource.allIds,
        multiple: true,
        visitors_from_all_sites: true
      },
      {
        width: '1200px'
      },
      (results) => {
        if (typeof results != 'undefined') {
          // Set the newly selected ids.
          this.dataSource.allIds = results;
          // Send API request to sync contractors.
          this.api
            .makeRequest('put', `v2/assets/${this.asset_id}/users`, {
              ids: this.dataSource.allIds
            })
            .then(() => {
              // Refresh the list.
              this.dataSource.getData();
            })
            .catch((errorResponse) => {
              this.utils.handleAPIErrors(errorResponse);
            });
        }
      }
    );
  }

  /**
   * Unlinks the selected users.
   *
   * @return {void}
   */
  onUnlinkSelected(): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to unlink users from this asset.'
      );
      return;
    }

    this.utils.showModal(
      'Unlink Selected Users',
      'Are you sure you want to unlink the selected Users?',
      () => {
        this.unlink(
          this.dataSource.selection.selected.join(','),
          'User were unlinked'
        );
      }
    );
  }

  /**
   * Unlinks the selected user.
   *
   * @param {number} id - The ID of the user to be unlinked.
   * @return {void}
   */
  onUnlink(id: number): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to unlink users from this asset.'
      );
      return;
    }

    this.utils.showModal(
      'Unlink Selected User',
      'Are you sure you want to unlink the selected User?',
      () => {
        this.unlink(id, 'User was unlinked');
      }
    );
  }

  /**
   * Unlinks users with the specified IDs.
   *
   * @param {any} ids - The contractor IDs to be unlinked.
   * @param msg
   * @return {void}
   */
  private unlink(ids: any, msg: string): void {
    this.api
      .makeRequest(
        'delete',
        `v2/assets/${this.asset_id}/users`,
        {},
        {
          user_ids: ids
        }
      )
      .then(() => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Show a component dialog for filtering users.
   *
   * @return {void}
   */
  onFilter(): void {
    this.utils.showComponentDialog(
      AssetsViewUsersFilterComponent,
      {
        trade: this.dataSource.trade
      },
      {
        width: '500px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.dataSource.trade = results.trade;
          this.dataSource.getData();
        }
      }
    );
  }

  /**
   * Open the user's public profile dialog.
   *
   * @param hash
   */
  onUserPublicView(hash: string): void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }
}

export class AssetsViewUsersDataSource extends ApiDataSource {
  order_by = 'date_created';
  order = 'desc';
  asset_id: number;
  trade: string = '';

  getData(resetOffset: boolean = false) {
    this.makeRequest(`v2/assets/${this.asset_id}/users`, resetOffset, {
      trade: this.trade
    });
  }
}
